<template>
	<div class="fd-consents">
		<div class="fd-consent" v-for="ct in ctypes" :key="ct.id" :style="consentStyle(ct)">
			<sym-icon class="fd-consent-icon" :name="ct.icon" :style="{ color: ct.iconColor }"></sym-icon>
			<span class="fd-consent-name">{{ ct.name }}</span>
			<span class="fd-consent-timestamp">{{ ct.timestamp }}</span>
			<span v-if="ct.description" class="fd-consent-description fd-help">{{ ct.description }}</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import useEditor, {type EditorProps, editorProps} from "./Editor";
import type {Consent, ConsentTypeSorted} from "@t/customer/Consent";
import {ConsentSource} from "@t/customer/Consent";
import type {ConsentPart} from "@t/fd/fixed/Consent";
import type {ParsedDate} from "@t/util/DateUtil";
import {parseDate} from "@t/util/DateUtil";
import {computed, type CSSProperties} from "vue";
import SymIcon from "../helpers/SymIcon.vue";
import {injectEnv} from "../Environment";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;
const env = injectEnv();

const con = computed<ConsentPart>(() => props.part as ConsentPart);

const {visible, value, clazz, style, ro} = useEditor(props);

const consents = computed<Consent[]>(() => {
	if (Array.isArray(value.value)) {
		return value.value;
	} else return [];
});

export interface ConsentDisplay extends ConsentTypeSorted {
	icon: string;
	iconColor: string;
	timestamp: string;
	description?: string;
}


function findConsent(consentData: Consent[], typeId: number): Consent {
	for (let c of consentData) {
		if (c.type === typeId) return c;
	}
	return {name: "", type: typeId, source: ConsentSource.UNKNOWN, value: false, entered: null, enteredBy: null};
}

function formattedDate(d: ParsedDate): string {
	if (d == null || d.date == null) return "";
	if (d.wholeDay) return env.formats.dateFormat.format(d.date);
	else return env.formats.dateTimeFormat.format(d.date);
}

const ctypes = computed<ConsentDisplay[]>(() => {
	const res: ConsentDisplay[] = [];
	for (let t of con.value.types) {
		const c = findConsent(consents.value, t.id);
		let ic = "consent";
		let col = "var(--orange)";
		let stamp = "";
		if (c.entered) {
			if (c.value) {
				ic = "consent-yes";
				col = "var(--green)";
			} else {
				ic = "consent-no";
				col = "var(--red)";
			}
			const d = parseDate(c.entered);	// from JSON
			stamp = formattedDate(d);
		}
		res.push({
			...t,
			icon: ic,
			iconColor: col,
			timestamp: stamp,
			description: c.description,
		});
	}
	return res;
});

function consentStyle(type: ConsentDisplay): CSSProperties {
	const s: CSSProperties = {};
	if (type.level) s.marginLeft = (type.level * 22) + "px";
	return s;
};

</script>

<style>
.fd-consents {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	row-gap: calc(2 * var(--form-v-padding));
}

.fd-consent {
	display: grid;
	grid-template-columns: 22px auto auto;
}

.fd-consent-icon {
	grid-column: 1 / 2;
}

.fd-consent-name {
	grid-column: 2 / 3;
}

.fd-consent-timestamp {
	grid-column: 3 /4;
	padding-left: 1em;
	justify-self: right;
	font-size: var(--font-size-s);
}

.fd-consent-description {
	grid-column: 2 / 4;
}

</style>
